/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEmailMetricsByWorkspace } from '@/api/dashboard/useGetEmailMetricsByWorkspace';
import { useAppSelector } from '@/app/hooks';
import BigColoredText from '@/component-library/primitives/BigColoredText/BigColoredText';
import CircleBarWithText from '@/component-library/primitives/CircleBarWithText/CircleBarWithText';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';
import LayersIcon from '@/component-library/primitives/Icons/LayersIcon/LayersIcon';
import Loader from '@/component-library/primitives/Loader/Loader';
import BarChart from '@/component-library/widgets/BarChart/BarChart';
import { Input } from '@/component-library/widgets/Input/Input';
import PieChart from '@/component-library/widgets/PieChart/PieChart';
import TabMenu from '@/component-library/widgets/TabMenu/TabMenu';
import { usePageTracking } from '@/hooks/useTracking';
import DashboardSection from '@/modules/Dashboard/DashboardPage/DashboardSection/DashboardSection';
import DeliveryTableMini from '@/modules/Dashboard/Delivery/DeliveryTableMini/DeliveryTableMini';
import { roundNumber } from '@/utils/numberUtils';

import { selectCurrentWorkspace } from '../Workspaces/workspaceSlice';

import './styles.css';

const SingleSendsMetricsPage = () => {
  const { t } = useTranslation();
  const selectedWorkspace = useAppSelector(selectCurrentWorkspace);

  usePageTracking('single-sends-metrics', { component: 'single-sends-metrics' });

  const tzoffset = new Date().getTimezoneOffset() * 60000;
  const [startDate, setStartDate] = useState(
    new Date(new Date(selectedWorkspace?.createdAt ?? Date.now()).getTime() - tzoffset).toISOString().slice(0, -8),
  );
  const [endDate, setEndDate] = useState(new Date(Date.now() - tzoffset).toISOString().slice(0, -8));

  const formattedStartDate = !isNaN(new Date(startDate).getTime()) ? new Date(startDate).toISOString() : '';
  const formattedEndDate = !isNaN(new Date(endDate).getTime()) ? new Date(endDate).toISOString() : '';

  const { data, isLoading } = useGetEmailMetricsByWorkspace(formattedStartDate, formattedEndDate);

  const timelineWithoutZeroes = useMemo(
    () => data?.data.emailMetrics.timeline.data.filter((item) => item.delivered > 0) ?? [],
    [data],
  );

  const tabLabels: string[] = [t('SideMenu.linkEmail'), t('SideMenu.linkSMS'), t('SideMenu.linkPushNotification')];
  const [activeStep, setActiveStep] = useState<string>(tabLabels[0]);
  const changeActiveStep = (step: string) => setActiveStep(step);

  let errorMessage = '';
  if (new Date(startDate) > new Date(endDate)) {
    errorMessage = t('DashboardPage.endDateAfterStartDateErrorMsg');
  } else if (new Date(endDate) > new Date()) {
    errorMessage = t('DashboardPage.endDateAfterCurrentDateErrorMsg');
  }

  const metrics = data?.data.emailMetrics;

  return (
    <div className="flex flex-col h-full">
      <div className="mb-2">
        <h1 className="text-xl font-semibold text-custom-black">{t('SingleSendsMetricsPage.title')}</h1>
        <p className="mt-1 text-sm text-custom-darkSilver">{t('SingleSendsMetricsPage.subtitle')}</p>
      </div>
      <header className="flex items-center justify-between">
        <TabMenu tabLabels={tabLabels} activeStep={activeStep} changeActiveStep={changeActiveStep} />
      </header>

      <div className="mt-2 flex-1 min-h-0 relative">
        {activeStep === t('SideMenu.linkEmail') && (
          <div className="grid-container">
            {/* Date filters */}
            <div className="email-header mb-8">
              <div className="flex items-center gap-x-8 mt-4">
                <Input
                  label={t('DashboardPage.startDateLabel')}
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label={t('DashboardPage.endDateLabel')}
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              {errorMessage && (
                <p className="text-errorRed-500 mt-2 text-xs font-medium flex items-center gap-2">
                  <CircleWarningIcon /> {errorMessage}
                </p>
              )}
            </div>

            {/* Loader / empty / error states */}
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader />
              </div>
            ) : errorMessage ? null : (
              <>
                {/* Top Summary Metrics */}
                <div className="total-sent">
                  <DashboardSection title={t('DashboardPage.total')} contentCentered>
                    <BigColoredText number={metrics?.totals.totalSent} color="#7E6CF5" />
                  </DashboardSection>
                </div>

                <div className="total-delivered">
                  <DashboardSection title={t('DashboardPage.totalDelivered')}>
                    <BigColoredText number={metrics?.totals.totalDelivered} color="#529F7C" />
                  </DashboardSection>
                </div>

                <div className="delivery-rate">
                  <DashboardSection title={t('DashboardPage.deliveryRate')}>
                    <CircleBarWithText
                      circleSvgId="delivery-rate"
                      fillPercentage={roundNumber(metrics?.rates.deliveryRate ?? 0)}
                      color="#529F7C"
                      textColor="#529F7C"
                    />
                  </DashboardSection>
                </div>

                {/* Delivery Table */}
                <div className="delivery-table">
                  <DashboardSection
                    titleCentered={false}
                    contentCentered
                    title={
                      <div className="flex items-center gap-x-2">
                        <LayersIcon />
                        <h3 className="text-custom-black font-medium text-xl">{t('DashboardPage.delivery')}</h3>
                      </div>
                    }
                  >
                    <DeliveryTableMini data={metrics?.deliveries} />
                  </DashboardSection>
                </div>

                {/* Open & Click Rates */}
                <div className="open-rate">
                  <DashboardSection title={t('DashboardPage.openRate')}>
                    <CircleBarWithText
                      circleSvgId="open-to-delivered-rate"
                      fillPercentage={roundNumber(metrics?.rates.openRate ?? 0)}
                      color="#9BEBC6"
                      textColor="#9BEBC6"
                    />
                  </DashboardSection>
                </div>

                <div className="total-opened">
                  <DashboardSection title={t('DashboardPage.totalOpened')}>
                    <BigColoredText number={metrics?.totals.totalOpened} color="#9BEBC6" />
                  </DashboardSection>
                </div>

                <div className="click-to-open">
                  <DashboardSection title={t('DashboardPage.clickToOpenRate')}>
                    <BigColoredText
                      number={roundNumber(metrics?.rates.clickToOpenRate ?? 0)}
                      color="#7898FB"
                      numberSizePx={60}
                      withPercentage
                    />
                  </DashboardSection>
                </div>

                <div className="complaint-rate">
                  <DashboardSection title={t('DashboardPage.complaintRate')} contentEnd>
                    <BigColoredText
                      number={roundNumber(metrics?.rates.complaintRate ?? 0)}
                      color="#E87171"
                      numberSizePx={60}
                      withPercentage
                    />
                  </DashboardSection>
                </div>

                {/* Delivery by Status Pie */}
                <div className="delivery-status">
                  <DashboardSection title={t('DashboardPage.deliveryByStatus')} titleCentered={false} contentCentered>
                    <PieChart
                      data={[
                        {
                          name: 'Opened',
                          value: roundNumber(metrics?.deliveryByStatus.opened ?? 0),
                        },
                        {
                          name: 'Not opened',
                          value: roundNumber(metrics?.deliveryByStatus.notOpened ?? 0),
                        },
                        {
                          name: 'Bounced',
                          value: roundNumber(metrics?.deliveryByStatus.bounced ?? 0),
                        },
                        {
                          name: 'Rejected',
                          value: roundNumber(metrics?.deliveryByStatus.rejected ?? 0),
                        },
                        {
                          name: 'Complaints',
                          value: roundNumber(metrics?.deliveryByStatus.complaints ?? 0),
                        },
                      ]}
                      colors={[
                        { name: 'Opened', value: '#9BEBC6' },
                        { name: 'Not opened', value: '#C5CCD8' },
                        { name: 'Bounced', value: '#F5F069' },
                        { name: 'Rejected', value: '#FFC271' },
                        { name: 'Complaints', value: '#E87171' },
                      ]}
                    />
                  </DashboardSection>
                </div>

                {/* Delivered vs Opened BarChart */}
                <div className="delivered-vs-opened">
                  <DashboardSection title={t('DashboardPage.deliveredVsOpened')}>
                    {timelineWithoutZeroes.length ? (
                      <BarChart data={timelineWithoutZeroes} />
                    ) : (
                      <p className="text-sm text-custom-darkSilver">{t('SingleSendsMetricsPage.noTimelineData')}</p>
                    )}
                  </DashboardSection>
                </div>
              </>
            )}
          </div>
        )}

        {activeStep === t('SideMenu.linkSMS') && (
          <div className="rounded-xl border border-custom-lightSilver bg-custom-silver/30 px-8 py-12 text-center">
            <p className="text-sm text-custom-darkSilver">{t('SingleSendsMetricsPage.smsPlaceholder')}</p>
          </div>
        )}

        {activeStep === t('SideMenu.linkPushNotification') && (
          <div className="rounded-xl border border-custom-lightSilver bg-custom-silver/30 px-8 py-12 text-center">
            <p className="text-sm text-custom-darkSilver">{t('SingleSendsMetricsPage.pushPlaceholder')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleSendsMetricsPage;
